import type { IDocumentResponse, IFile, IGraphData, IProjectDetail } from '@/domains/project/types';
import { PsHttpService } from '@patsnap/tahiti-core';
import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface DetailApi {
  getProjectDetail(projectId: string): Promise<IProjectDetail>;
  getKG(projectId: string): Promise<IGraphData | undefined>;
  getMoreKG(projectId: string, nodeId: string, nodeName: string): Promise<IGraphData | undefined>;
  getDocuments(
    projectId: string,
    nodeIds: string[],
    display_type: string,
    page: number,
    limit: number,
  ): Promise<IDocumentResponse>;
  getRelationNode(
    projectId: string,
    nodeId: string,
    nodeName: string,
  ): Promise<string[] | undefined>;
  getSeekQuestions(projectId: string, nodeId: string, nodeName: string): Promise<string[]>;
  getSeekRecommend(projectId: string): Promise<string[]>;
  editFile(projectId: string, fileId: string, fileName: string): Promise<void>;
  refreshFile(
    projectId: string,
    fileId: string,
    retryType: string,
    contentFormatType?: string,
  ): Promise<void>;
  getFilesStatus(projectId: string, fileIds: string[]): Promise<IFile[]>;
  uploadFile(projectId: string, file: File): Promise<{ item_id: string }>;
  getExamples(projectId: string): Promise<string[]>;
  getPdfPath(itemId: string, projectId: string): Promise<string>;
  getLsDocumentAgentData(data: {
    business_id: string;
    business_type: 'ls_file' | 'ls_file_chat' | 'RD' | 'IP' | 'MSE' | 'LS';
    item_ids: string[];
    work_project_id: string;
  }): Promise<{ chat_id: string; file_id: string }>;
}

@injectable()
export class DetailApiImpl extends PsHttpService implements DetailApi {
  get options() {
    return {
      baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/work/project`,
    };
  }

  getProjectDetail(projectId: string): Promise<IProjectDetail> {
    return this.get<PatSnapCommonResponseData<IProjectDetail>>({
      url: `/${projectId}`,
    })
      .then((res) => {
        return res.data as IProjectDetail;
      })
      .catch(() => {
        return {} as IProjectDetail;
      });
  }

  getKG(projectId: string): Promise<IGraphData | undefined> {
    return this.post<PatSnapCommonResponseData<IGraphData>>({
      url: '/graph/init',
      data: { work_project_id: projectId },
    }).then((res) => {
      return res.data as IGraphData;
    });
  }

  getMoreKG(projectId: string, nodeId: string, nodeName: string): Promise<IGraphData | undefined> {
    return this.post<PatSnapCommonResponseData<IGraphData>>({
      url: '/graph/expand',
      data: { work_project_id: projectId, node_id: nodeId, node_name: nodeName },
    }).then((res) => {
      return res.data as IGraphData;
    });
  }

  getRelationNode(
    projectId: string,
    nodeId: string,
    nodeName: string,
  ): Promise<string[] | undefined> {
    return this.post<PatSnapCommonResponseData<string[]>>({
      url: '/graph/suggest',
      data: { work_project_id: projectId, node_id: nodeId, node_name: nodeName },
    }).then((res) => {
      return res.data as string[];
    });
  }

  getDocuments(
    projectId: string,
    nodeIds: string[],
    display_type: string,
    page: number,
    limit: number,
  ): Promise<IDocumentResponse> {
    return this.post<PatSnapCommonResponseData<IDocumentResponse>>({
      url: '/item/list',
      data: { work_project_id: projectId, node_ids: nodeIds, page, limit, display_type },
    })
      .then((res) => {
        return res.data as IDocumentResponse;
      })
      .catch(() => {
        return { rows: undefined, total: 0, page, limit } as IDocumentResponse;
      });
  }

  getSeekQuestions(projectId: string, nodeId: string, nodeName: string): Promise<string[]> {
    return this.post<PatSnapCommonResponseData<string[]>>({
      url: '/graph/seek',
      data: { work_project_id: projectId, node_id: nodeId, node_name: nodeName },
    }).then((res) => {
      return res.data as string[];
    });
  }

  // 重命名
  editFile(projectId: string, fileId: string, fileName: string): Promise<void> {
    return this.post<PatSnapCommonResponseData<void>>({
      url: '/item/update-title',
      data: { item_id: fileId, file_name: fileName, work_project_id: projectId },
    }).then(() => {});
  }

  // 重新解析文件
  refreshFile(
    projectId: string,
    fileId: string,
    retryType: 'agent' | 'upload',
    contentFormatType?: string,
  ): Promise<void> {
    return this.post<PatSnapCommonResponseData<void>>({
      url: '/item/retry-processing',
      data: {
        work_project_id: projectId,
        item_id: fileId,
        retry_type: retryType,
        content_format_type: contentFormatType,
      },
    }).then(() => {});
  }

  // 获取文件状态
  getFilesStatus(projectId: string, fileIds: string[]): Promise<IFile[]> {
    return this.post<PatSnapCommonResponseData<IFile[]>>({
      url: '/item/process/status',
      data: { work_project_id: projectId, item_ids: fileIds },
    }).then((res) => {
      return res.data as IFile[];
    });
  }

  // 文件上传
  uploadFile(projectId: string, file: File): Promise<{ item_id: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.post<PatSnapCommonResponseData<{ item_id: string }>>({
      url: '/item/upload-file?work_project_id=' + projectId,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((res) => {
      return res.data as { item_id: string };
    });
  }

  // 获取推荐问题 - 基于项目内容的个性化推荐
  getSeekRecommend(projectId: string): Promise<string[]> {
    return this.get<PatSnapCommonResponseData<string[]>>({
      url: '/seek-recommend',
      params: {
        work_project_id: projectId,
      },
    }).then((res) => {
      return res.data as string[];
    });
  }

  // 获取示例问题
  getExamples(projectId: string): Promise<string[]> {
    return this.get<PatSnapCommonResponseData<string[]>>({
      url: '/question',
      params: {
        work_project_id: projectId,
      },
    }).then((res) => {
      return res.data as string[];
    });
  }

  getPdfPath(itemId: string, projectId: string): Promise<string> {
    return this.get<PatSnapCommonResponseData<string>>({
      url: '/item/file-path',
      params: { item_id: itemId, work_project_id: projectId },
    }).then((res) => {
      return res.data as string;
    });
  }

  getLsDocumentAgentData(data: {
    business_id: string;
    business_type: 'RD' | 'IP' | 'MSE' | 'LS';
    item_ids: string[];
    work_project_id: string;
  }) {
    return this.post<PatSnapCommonResponseData<{ chat_id: string; file_id: string }>>({
      url: '/item/init-ls',
      data,
    }).then((res) => {
      return res.data as { chat_id: string; file_id: string };
    });
  }
}

export const DetailApi = Symbol('DetailApi');
